<template>
    <md-list-item :class="{ 'ql-version-fresh': fresh, 'ql-version-editing': editing }">
        <!-- No icon while renaming: the name field gets that space too. -->
        <md-icon v-if="isCurrentVersion(version.id) && !editing">label</md-icon>
        <md-icon v-else-if="autoKind && !editing">history</md-icon>
        <md-icon v-else-if="!editing">label_outline</md-icon>
        <div class="md-list-text-container">
            <span v-if="!editing" class="ql-version-name" :class="{ 'ql-version-name-editable': canEditName }" @click.stop="startEdit">{{displayName}}<md-tooltip v-if="canEditName" md-direction="top">{{autoKind ? 'Click to rename and keep' : 'Click to rename'}}</md-tooltip></span>
            <form v-else novalidate class="ql-version-rename" @submit.stop.prevent="saveName">
                <md-input-container>
                    <md-input v-model="editName" ref="renameInput" @click.stop></md-input>
                </md-input-container>
            </form>
            <span v-if="autoKind">{{autoDetail}}<md-tooltip md-direction="bottom">{{autoDetail}}</md-tooltip></span>
            <span v-else>{{getFormattedDate(version)}}<md-tooltip md-direction="bottom">{{getFormattedDate(version)}}</md-tooltip></span>
        </div>
        <md-button class="md-icon-button md-list-action" @click.stop.prevent="saveName" v-if="editing && canEditName">
            <md-icon>check</md-icon>
            <md-tooltip md-direction="top">Save name</md-tooltip>
        </md-button>
        <md-button class="md-icon-button md-list-action" @click="deleteVersion(version.id)" 
            v-if="!editing && (user.uid === ownerUID || user.uid === version.creator) && activeProjectEntitled">
            <md-icon>delete</md-icon>
            <md-tooltip md-direction="top">Delete Version</md-tooltip>
        </md-button>
        <md-button class="md-icon-button md-list-action" @click="promoteVersion(version.id)" 
            v-if="!editing && user.uid === ownerUID && activeProjectEntitled">
            <md-icon>vertical_align_top</md-icon>
            <md-tooltip md-direction="top">Make Live</md-tooltip>
        </md-button>
        <md-button class="md-icon-button md-list-action" @click="setActiveVersion(version.id)" v-if="!editing">
            <md-icon>launch</md-icon>
            <md-tooltip md-direction="top">Show Version</md-tooltip>
        </md-button>
    </md-list-item>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { rowTitle, rowDetail } from '@/lib/history-autosave';

const Version = {
    name: 'Version',
    // autoKind: set when the row sits in an automatic-saves folder ('page_op' or
    // 'promote_backup'). fresh: the row was just saved, tint it.
    props: ['project', 'version', 'autoKind', 'fresh'],
    data: function() {
        return {
            editing: false,
            editName: ''
        };
    },
    computed: {
        ...mapGetters([
            'ownerUID',
            'user',
            'versions',
            'currentVersion',
            'activeProjectEntitled'
        ]),
        displayName() {
            return this.autoKind ? rowTitle(this.version, this.autoKind) : this.version.name;
        },
        autoDetail() {
            return rowDetail(this.version, this.autoKind);
        },
        // Same permission as deleting a version: the owner or the version's creator,
        // with an active plan.
        canEditName() {
            return (this.user.uid === this.ownerUID || this.user.uid === this.version.creator)
                && this.activeProjectEntitled;
        }
    },
    methods: {
        startEdit() {
            if (!this.canEditName) return;
            this.editName = this.displayName || '';
            this.editing = true;
            this.$nextTick(() => {
                const ref = this.$refs.renameInput;
                const el = ref && ref.$el ? (ref.$el.querySelector('input') || ref.$el) : null;
                if (el && el.focus) el.focus();
            });
        },
        saveName() {
            if (!this.editing) return;
            const name = (this.editName || '').trim();
            this.editing = false;
            if (name && name !== this.version.name) {
                this.$store.dispatch('renameVersion', { versionID: this.version.id, name });
            }
        },
        setActiveVersion(versionID) {
            if (versionID === null) {
                window.location.href = `/project/${this.project.id}`;
                return;
            }

            const vIndex = this.versions.findIndex(v => v.id === versionID);
            if (vIndex > -1) {
                window.location.href = `/project/${this.project.id}/version/${versionID}/`;
            }
        },
        isCurrentVersion(versionID) {
            if (this.currentVersion) {
                return this.currentVersion.id === versionID;
            }

            return false;
        },
        // The panel stays open (Josh, 5 Oct), whatever the confirm answer, so several
        // versions can be tidied in one go. Make live still closes it: the project reloads.
        deleteVersion(versionID) {
            this.$emit('deleteVersion', versionID);
        },
        async promoteVersion(versionID) {
            this.$emit('close');
            this.$store.dispatch('setLoader', { type: true });
            await this.$emit('promoteVersion', versionID);
            this.$store.dispatch('setLoader', { type: false });
        },
        getFormattedDate(version) {
            return moment(version.created).format('MM/DD/YYYY hh:mm:ss a');
        }
    }
};
export default Version;

</script>

<style lang="scss">
.ql-version-list {
    .md-list-item {
        margin-top: .75em;
        line-height: 40px;
        .md-list-item-container > .md-icon:first-child  {
            margin-right: .5em !important;
        }
        .md-list-item-container > .md-button:first-child {
            margin-left: 0 !important;
        } 
    }
}
.md-sidenav-content > form {
    padding-left: .5em;
}
span.md-tooltip {
    z-index: 1000;
}
.ql-version-name-editable {
    cursor: pointer;
}
// Renaming: the row's other buttons step aside so the name field gets the whole row
// (Josh, 6 Oct: with them showing, only ~8 characters were visible while typing).
.ql-version-list .md-list-item.ql-version-editing .md-list-text-container {
    flex: 1 1 auto;
    min-width: 0;
}
.ql-version-rename {
    width: 100%;
    .md-input-container {
        width: 100%;
        margin: 0;
    }
    .md-input-container input {
        font-size: 14px !important;
    }
}
// The row container paints its own white background, so tint that, not the li.
.ql-version-list .md-list-item.ql-version-fresh > .md-list-item-container {
    background: #e7f3ec;
    box-shadow: inset 3px 0 0 #2e7d4f;
}
</style>


// WEBPACK FOOTER //
// version.vue?90fec146